import React, { useState } from "react";
import {
  ArrowRight,
  Check,
  ChevronDown,
  TrendingUp,
  ShieldCheck,
  CalendarClock,
  Inbox,
  Users2,
  Bell,
  Mail,
} from "lucide-react";
import "./EMC45002.scss";

type Cycle = "monthly" | "annual";

interface Tier {
  key: string;
  name: string;
  blurb: string;
  monthly: number;
  annual: number;
  cta: string;
  highlighted?: boolean;
  features: string[];
}

const TIERS: Tier[] = [
  {
    key: "personal",
    name: "Personal",
    blurb: "For individuals managing their own inbox and schedule",
    monthly: 8,
    annual: 6,
    cta: "Start free trial",
    features: [
      "1 mailbox, 1 calendar",
      "Unified inbox & smart scheduling",
      "Booking links",
      "Focus time protection",
      "Email support",
    ],
  },
  {
    key: "team",
    name: "Team",
    blurb: "For teams who live and die by their calendars",
    monthly: 16,
    annual: 13,
    cta: "Start free trial",
    highlighted: true,
    features: [
      "Up to 50 mailboxes",
      "Shared team calendars",
      "Round-robin meeting routing",
      "Meeting analytics & no-show tracking",
      "Custom domains & branded booking pages",
      "Priority support",
    ],
  },
  {
    key: "enterprise",
    name: "Enterprise",
    blurb: "For organizations with complex scheduling and compliance needs",
    monthly: 0,
    annual: 0,
    cta: "Talk to sales",
    features: [
      "Unlimited mailboxes",
      "SSO & SCIM provisioning",
      "Org-wide retention & legal hold",
      "Dedicated success manager",
      "99.95% uptime SLA",
      "Custom integrations & API limits",
    ],
  },
];

interface AccordionItem {
  title: string;
  body: string;
}

const OVERVIEW_TABS: { label: string; items: AccordionItem[] }[] = [
  {
    label: "Key benefits",
    items: [
      {
        title: "Together in one place",
        body: "See everything you need to manage your day in one view. Easily stay on top of emails, calendars, contacts, and to-do lists — at home or on the go, all inside Cilprom Mail.",
      },
      {
        title: "Connect your accounts",
        body: "Add every mailbox you own — work, personal, or shared — and Cilprom Mail folds them into a single inbox without losing track of which account each message came from.",
      },
      {
        title: "Write more effective emails",
        body: "Built-in tone and clarity suggestions help you get to the point faster, so replies go out in less time and land better with the people reading them.",
      },
    ],
  },
  {
    label: "Get more from it",
    items: [
      {
        title: "An inbox that stays out of your way",
        body: "Cilprom Mail Premium clears out promotional clutter automatically and keeps your focused view free of anything you didn't ask to see.",
      },
      {
        title: "Storage that grows with you",
        body: "Attachments, photos, and years of threads stay archived and searchable, with extra cloud storage shared across every connected account.",
      },
      {
        title: "Rules that actually hold",
        body: "Build multi-condition filters once — by sender, subject, or keyword — and Cilprom Mail applies them consistently across every device you use.",
      },
    ],
  },
  {
    label: "Smart assistant",
    items: [
      {
        title: "Drafts in your own voice",
        body: "The assistant reads the thread and proposes a reply that matches how you actually write, ready to edit or send as-is.",
      },
      {
        title: "Summaries for long threads",
        body: "Catch up on a 40-message thread in a few lines, with the decisions and open questions pulled out for you.",
      },
      {
        title: "Scheduling suggestions",
        body: "The assistant reads free/busy across everyone on the thread and proposes times that actually work, before anyone has to ask.",
      },
    ],
  },
];

const FEATURES = [
  {
    icon: Inbox,
    title: "One inbox for every account you run",
    body: "Connect every mailbox you manage into a single view, with shared labels, snoozing, and templates that sync across the whole team.",
  },
  {
    icon: CalendarClock,
    title: "Scheduling that finds the slot for you",
    body: "Booking links read everyone's real availability across time zones, route round-robin, and rebook automatically when someone cancels.",
  },
  {
    icon: Bell,
    title: "Focus time that actually holds",
    body: "Protected blocks decline conflicting invites automatically and explain why, so deep work stays on the calendar.",
  },
];

const STATS = [
  { value: "4.6 hrs", label: "saved per person, per week, on scheduling back-and-forth" },
  { value: "31%", label: "fewer no-shows after enabling automated reminders" },
  { value: "99.95%", label: "platform uptime, backed by SLA" },
  { value: "8,900+", label: "teams running their inbox and calendar on the platform" },
];

const STORIES = [
  {
    company: "Marlowe & Voss",
    industry: "Recruiting",
    metric: "+38% interviews scheduled per recruiter",
    quote:
      "Round-robin booking links alone gave every recruiter back an hour a day. Candidates book themselves into the right interviewer's real availability.",
    person: "Head of Talent Operations",
  },
  {
    company: "Aldergate Partners",
    industry: "Consulting",
    metric: "No-shows down 31% in one quarter",
    quote:
      "Automated reminders and one-click rescheduling did what a dozen manual follow-up emails never could.",
    person: "Operations Director",
  },
  {
    company: "Brightwell Clinic Group",
    industry: "Healthcare",
    metric: "12 locations on one shared calendar",
    quote:
      "We run twelve clinics with overlapping staff. Shared calendars with real conflict detection replaced a whiteboard and three spreadsheets.",
    person: "Practice Manager",
  },
];

const FAQS = [
  {
    q: "Does it work with our existing Gmail or Cilprom Mail accounts?",
    a: "Yes. The platform connects directly to Gmail, Google Workspace, and Cilprom Mail — your existing addresses and calendars stay exactly where they are.",
  },
  {
    q: "Can booking links handle multiple time zones?",
    a: "Booking pages detect the visitor's time zone automatically and always display real-time availability pulled live from connected calendars.",
  },
  {
    q: "How does round-robin routing decide who gets the meeting?",
    a: "You can route by even rotation, lowest current load, or custom rules. Routing logic is configurable per booking page on Team and Enterprise plans.",
  },
  {
    q: "Is our email and calendar data secure?",
    a: "Data is encrypted in transit and at rest, with SOC 2 Type II compliance. Enterprise plans add SSO/SCIM, retention policies, and legal hold.",
  },
];

/** Small reusable "inbox" mockup rendered inside each device screen. */
const MailScreen: React.FC<{ rows?: number; folders?: number }> = ({
  rows = 4,
  folders = 4,
}) => {
  const avatarColors = ["#93c5fd", "#fca5a5", "#86efac", "#fcd34d", "#c4b5fd"];
  return (
    <div className="emc-mock-main-wrap">
      {folders > 0 && (
        <div className="emc-mock-sidebar">
          {Array.from({ length: folders }).map((_, i) => (
            <div key={i} className={i === 0 ? "emc-mock-folder active" : "emc-mock-folder"} />
          ))}
        </div>
      )}
      <div className="emc-mock-main">
        {Array.from({ length: rows }).map((_, i) => (
          <div key={i} className={i < 2 ? "emc-mail-row unread" : "emc-mail-row"}>
            <div
              className="emc-mail-avatar"
              style={{ background: avatarColors[i % avatarColors.length] }}
            />
            <div className="emc-mail-lines">
              <div className="emc-mail-line title" />
              <div className="emc-mail-line short" />
            </div>
            <div className="emc-mail-time" />
          </div>
        ))}
      </div>
    </div>
  );
};

const EMC45002: React.FC = () => {
  const [cycle, setCycle] = useState<Cycle>("monthly");
  const [openFaq, setOpenFaq] = useState<number | null>(0);
  const [activeTab, setActiveTab] = useState(0);
  const [openAcc, setOpenAcc] = useState<number | null>(0);

  const handleTabChange = (i: number) => {
    setActiveTab(i);
    setOpenAcc(0);
  };

  // Every CTA on this page routes here.
  const SIGNUP_URL = "/auth/signup";
  const goToSignup = () => {
    window.location.href = SIGNUP_URL;
  };

  return (
    <div className="emc-page">
      {/* ================= HERO ================= */}
      <section className="emc-hero">
        <div className="emc-hero-bg" />
        <div className="emc-hero-inner">
          <div className="emc-hero-copy">
            <h1>
              Every inbox.
              <br />
              One calendar. One place.
            </h1>
            <p className="emc-hero-sub">
              Cilprom Mail brings every account, calendar, and contact you manage
              into a single view — so nothing slips through the cracks, on desktop,
              mobile, or web.
            </p>
            <button className="emc-link-btn" onClick={goToSignup}>
              <span>›</span> Create business email account
            </button>
          </div>

          {/* Right side – realistic layered device mockup */}
          <div className="emc-hero-visual">
            <div className="emc-device-stack">
              {/* Laptop */}
              <div className="emc-device-laptop">
                <div className="emc-laptop-screen-wrap">
                  <div className="emc-laptop-camera" />
                  <div className="emc-laptop-screen">
                    <div className="emc-device-header">
                      <span className="emc-dot" />
                      <span className="emc-dot" />
                      <span className="emc-dot" />
                    </div>
                    <div className="emc-device-body">
                      <MailScreen rows={5} folders={5} />
                    </div>
                  </div>
                </div>
                <div className="emc-laptop-base" />
                <div className="emc-laptop-shadow" />
              </div>

              {/* Tablet */}
              <div className="emc-device-tablet">
                <div className="emc-tablet-body">
                  <div className="emc-tablet-camera" />
                  <div className="emc-tablet-screen">
                    <div className="emc-device-header dark">
                      <span className="emc-dot" />
                      <span className="emc-dot" />
                      <span className="emc-dot" />
                    </div>
                    <div className="emc-device-body light">
                      <MailScreen rows={3} folders={4} />
                    </div>
                  </div>
                </div>
              </div>

              {/* Phone */}
              <div className="emc-device-phone">
                <div className="emc-phone-body">
                  <div className="emc-phone-screen">
                    <div className="emc-phone-island" />
                    <div className="emc-device-body">
                      <MailScreen rows={3} folders={0} />
                    </div>
                    <div className="emc-phone-home-indicator" />
                  </div>
                  <div className="emc-phone-side-btn" />
                  <div className="emc-phone-vol-btn" />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= OVERVIEW / TABS ================= */}
      <section className="emc-overview">
        <div className="emc-overview-inner">
          <div className="emc-overview-label">OVERVIEW</div>
          <h2 className="emc-overview-title">
            Your inbox can change everything
          </h2>

          <div className="emc-tabs">
            {OVERVIEW_TABS.map((tab, i) => (
              <button
                key={tab.label}
                className={`emc-tab ${activeTab === i ? "active" : ""}`}
                onClick={() => handleTabChange(i)}
              >
                {tab.label}
              </button>
            ))}
          </div>

          <div className="emc-overview-content">
            <div className="emc-overview-left">
              <div className="emc-accordion">
                {OVERVIEW_TABS[activeTab].items.map((item, i) => (
                  <div className={openAcc === i ? "emc-acc-item open" : "emc-acc-item"} key={item.title}>
                    <button
                      className="emc-acc-header"
                      onClick={() => setOpenAcc(openAcc === i ? null : i)}
                      aria-expanded={openAcc === i}
                    >
                      {item.title}
                      <ChevronDown
                        size={18}
                        className={openAcc === i ? "emc-acc-chev open" : "emc-acc-chev"}
                      />
                    </button>
                    {openAcc === i && <div className="emc-acc-body">{item.body}</div>}
                  </div>
                ))}
              </div>
            </div>

            <div className="emc-overview-right">
              <div className="emc-product-shot">
                <div className="emc-shot-desktop">
                  <div className="emc-device-header">
                    <span className="emc-dot" />
                    <span className="emc-dot" />
                    <span className="emc-dot" />
                  </div>
                  <div className="emc-device-body light" style={{ height: "calc(100% - 26px)" }}>
                    <MailScreen rows={4} folders={4} />
                  </div>
                </div>
                <div className="emc-shot-mobile">
                  <div className="emc-device-body light" style={{ height: "100%", paddingTop: 20 }}>
                    <MailScreen rows={3} folders={0} />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= FEATURES ================= */}
      <section className="emc-section">
        <div className="emc-section-head">
          <span className="emc-eyebrow">Platform</span>
          <h2>Everything your team needs to stop losing time</h2>
        </div>

        <div className="emc-feature-grid">
          {FEATURES.map((f) => (
            <div className="emc-feature-card" key={f.title}>
              <div className="emc-feature-icon">
                <f.icon size={22} strokeWidth={1.75} />
              </div>
              <h3>{f.title}</h3>
              <p>{f.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ================= STATS ================= */}
      <section className="emc-stats">
        {STATS.map((s) => (
          <div className="emc-stat" key={s.label}>
            <div className="emc-stat-value">{s.value}</div>
            <div className="emc-stat-label">{s.label}</div>
          </div>
        ))}
      </section>

      {/* ================= PRICING ================= */}
      <section className="emc-section" id="pricing">
        <div className="emc-section-head">
          <span className="emc-eyebrow">Pricing</span>
          <h2>Plans that scale from one inbox to the whole org</h2>
          <p className="emc-section-sub">
            All plans include unlimited booking links, calendar sync, and core
            reminders.
          </p>

          <div className="emc-toggle">
            <button
              className={cycle === "monthly" ? "emc-toggle-btn active" : "emc-toggle-btn"}
              onClick={() => setCycle("monthly")}
            >
              Monthly
            </button>
            <button
              className={cycle === "annual" ? "emc-toggle-btn active" : "emc-toggle-btn"}
              onClick={() => setCycle("annual")}
            >
              Annual <span className="emc-save">Save 18%</span>
            </button>
          </div>
        </div>

        <div className="emc-pricing-grid">
          {TIERS.map((tier) => (
            <div
              className={tier.highlighted ? "emc-tier emc-tier-highlight" : "emc-tier"}
              key={tier.key}
            >
              {tier.highlighted && (
                <span className="emc-tier-badge">Most popular</span>
              )}
              <h3>{tier.name}</h3>
              <p className="emc-tier-blurb">{tier.blurb}</p>

              <div className="emc-tier-price">
                {tier.monthly === 0 ? (
                  <span className="emc-price-custom">Custom pricing</span>
                ) : (
                  <>
                    <span className="emc-price-amount">
                      ${cycle === "monthly" ? tier.monthly : tier.annual}
                    </span>
                    <span className="emc-price-period">/ seat / mo</span>
                  </>
                )}
              </div>

              <button
                className={
                  tier.highlighted
                    ? "emc-btn-primary full"
                    : "emc-btn-secondary full"
                }
                onClick={goToSignup}
              >
                {tier.cta}
              </button>

              <ul className="emc-tier-features">
                {tier.features.map((f) => (
                  <li key={f}>
                    <Check size={15} strokeWidth={2.25} />
                    {f}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </section>

      {/* ================= STORIES ================= */}
      <section className="emc-section">
        <div className="emc-section-head">
          <span className="emc-eyebrow">Customer stories</span>
          <h2>Results from teams running on the platform</h2>
        </div>

        <div className="emc-story-grid">
          {STORIES.map((s) => (
            <div className="emc-story-card" key={s.company}>
              <div className="emc-story-metric">
                <TrendingUp size={15} strokeWidth={2.25} />
                {s.metric}
              </div>
              <p className="emc-story-quote">“{s.quote}”</p>
              <div className="emc-story-attrib">
                <div className="emc-story-company">{s.company}</div>
                <div className="emc-story-role">
                  {s.person} · {s.industry}
                </div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ================= SECURITY ================= */}
      <section className="emc-security">
        <div className="emc-security-item">
          <ShieldCheck size={18} strokeWidth={1.75} />
          SOC 2 Type II compliant
        </div>
        <div className="emc-security-item">
          <Users2 size={18} strokeWidth={1.75} />
          SSO & SCIM provisioning
        </div>
        <div className="emc-security-item">
          <CalendarClock size={18} strokeWidth={1.75} />
          99.95% uptime SLA
        </div>
      </section>

      {/* ================= FAQ ================= */}
      <section className="emc-section emc-faq-section">
        <div className="emc-section-head">
          <span className="emc-eyebrow">Questions</span>
          <h2>Frequently asked questions</h2>
        </div>

        <div className="emc-faq-list">
          {FAQS.map((item, i) => (
            <div className="emc-faq-item" key={item.q}>
              <button
                className="emc-faq-q"
                onClick={() => setOpenFaq(openFaq === i ? null : i)}
                aria-expanded={openFaq === i}
              >
                {item.q}
                <ChevronDown
                  size={18}
                  className={openFaq === i ? "emc-faq-chev open" : "emc-faq-chev"}
                />
              </button>
              {openFaq === i && <p className="emc-faq-a">{item.a}</p>}
            </div>
          ))}
        </div>
      </section>

      {/* ================= FINAL CTA ================= */}
      <section className="emc-cta-band">
        <h2>Connect your inbox and calendar in minutes</h2>
        <p>Set up takes about five minutes. No credit card required.</p>
        <div className="emc-cta-actions">
          <button className="emc-btn-primary" onClick={goToSignup}>
            Start free trial <ArrowRight size={16} />
          </button>
          <button className="emc-btn-ghost" onClick={goToSignup}>Book a demo</button>
        </div>
      </section>
    </div>
  );
};

export default EMC45002;